/**
 * Chunk Loading Error Handler
 *
 * Handles ChunkLoadError that occurs when:
 * 1. New deployments invalidate old chunk files
 * 2. Network issues prevent chunk loading
 * 3. Browser cache contains stale chunk references
 *
 * When an AI Scribe recording is active, auto-reload is suppressed
 * to avoid destroying the recording session.
 */

import { recordingSafeReload } from '@/lib/utils/recording-safe-reload';

/**
 * Check if error is a chunk loading error
 */
function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'ChunkLoadError' ||
      error.message.includes('Loading chunk') ||
      error.message.includes('ChunkLoadError') ||
      // Network errors that could be chunk-related
      (error.message.includes('Failed to import') &&
        error.message.includes('/_next/static/')))
  );
}

/**
 * Check if error is related to stale chunks from deployment
 */
function isStaleChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;

  const url = error instanceof Error ? error.message : '';

  // Look for Next.js chunk patterns and 404/network errors
  return (
    url.includes('/_next/static/chunks/') ||
    url.includes('/_next/static/css/') ||
    url.includes('404') ||
    url.includes('net::ERR_')
  );
}

/**
 * Global error handler for chunk errors
 * Should be set up in the root layout or error boundary
 */
export function setupChunkErrorHandler(): () => void {
  const handler = (event: ErrorEvent) => {
    if (isChunkLoadError(event.error)) {
      console.warn('Global chunk error detected:', event.error);
      if (isStaleChunkError(event.error)) {
        recordingSafeReload();
      }
    }
  };

  const unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      console.warn('Global chunk rejection detected:', event.reason);
      if (isStaleChunkError(event.reason)) {
        recordingSafeReload();
      }
    }
  };

  // Listen for chunk loading errors
  window.addEventListener('error', handler);
  window.addEventListener('unhandledrejection', unhandledRejectionHandler);

  // Return cleanup function
  return () => {
    window.removeEventListener('error', handler);
    window.removeEventListener('unhandledrejection', unhandledRejectionHandler);
  };
}
